import React, { useState } from 'react';
import { keyframes, styled } from '@mui/system';
import Image from 'next/image';
import { useWindowSize } from '@hooks/useWindowSize';
import { getRoute } from '@utils';
import Link from 'next/link';
import { Button, Typography } from '@mui/material';
import { usePermissions } from '@hooks/usePermissions';

const growOnClick = keyframes`
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
      opacity: 0.5;
      }
    100% {
      transform: scale(1);
      opacity: 1;
    }
`;

const ImageContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'clicked',
})<{ clicked: boolean }>(({ clicked }) => ({
  position: 'relative',
  width: '100%',
  height: '100px',
  maxHeight: '100px',
  marginBottom: '16px',
  cursor: 'pointer',
  animation: clicked ? `${growOnClick} 0.25s` : undefined,
}));

const BannerContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});
const ShareTypography = styled(Typography)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: '#15365F',
  textAlign: 'center',
  [theme.breakpoints.up(990)]: {
    fontSize: '28px',
    lineHeight: '42px',
  },
  [theme.breakpoints.up(608)]: {
    fontSize: '24px',
    lineHeight: '36px',
  },
}));
const MoreInfoButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Poppins',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '0.15px',
  textAlign: 'center',
  textTransform: 'none',
  textDecorationLine: 'underline',
  color: '#0A7ABE',
  [theme.breakpoints.up(990)]: {
    fontSize: '16px',
    lineHeight: '24px',
  },
}));
const TextDiv = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '259px',
  [theme.breakpoints.up(990)]: {
    minWidth: '449px',
  },
  [theme.breakpoints.up(608)]: {
    minWidth: '386px',
  },
}));

const handleImageByWidth = (width: number) => {
  if (width >= 990) {
    return '/assets/images/banners/referralBannerBig.webp';
  }
  if (width >= 608) {
    return '/assets/images/banners/referralBannerMedium.webp';
  }
  return '/assets/images/banners/referralBannerSmall.webp';
};

export function ReferralBanner() {
  const { width } = useWindowSize();
  const [clicked, setClicked] = useState(false);

  const { userCan } = usePermissions();
  const isLocalVendor = userCan('LOCAL_VENDOR');
  if (isLocalVendor) {
    return null;
  }

  const handleClick = () => {
    setClicked(true);
  };

  return (
    <Link href={getRoute('referral')} passHref>
      <BannerContainer>
        <ImageContainer onClick={handleClick} clicked={clicked}>
          <Image
            src={handleImageByWidth(width)}
            alt="Referral Banner"
            layout="fill"
            priority={true}
          />
          <TextDiv>
            <ShareTypography>¡Comparte y gana descuentos!</ShareTypography>
            <MoreInfoButton>Más información</MoreInfoButton>
          </TextDiv>
        </ImageContainer>
      </BannerContainer>
    </Link>
  );
}
